/**
 * 赠送与赠课系统服务
 * 处理推荐赠送、购课达标赠送等台账相关 API 调用
 */

import apiClient from './apiClient';
import { API_ENDPOINTS } from '../config/api';

const rewardService = {
  /**
   * 获取学员赠送余额概览
   * 包含：正课余额、可用、在途
   * @param {number} studentId - 学员 ID
   */
  async getBalance(studentId) {
    return apiClient.get(API_ENDPOINTS.REWARDS.BALANCE(studentId));
  },

  /**
   * 获取全局流水明细（管理员视角，支持年份筛选、分页）
   * @param {Object} params - 查询参数
   * @param {number} params.page - 页码
   * @param {number} params.per_page - 每页数量
   * @param {string} params.year - 年份筛选
   * @param {string} params.source_type - 来源类型筛选
   * @param {string} params.action_type - 动作类型筛选
   */
  async getLedger(params = {}) {
    return apiClient.get(API_ENDPOINTS.REWARDS.LEDGER, {
      page: 1,
      per_page: 20,
      ...params,
    });
  },

  /**
   * 获取指定学员的流水明细（学员视角）
   * @param {number} studentId - 学员 ID
   */
  async getStudentLedger(studentId) {
    return apiClient.get(API_ENDPOINTS.REWARDS.STUDENT_LEDGER(studentId));
  },

  /**
   * 写入推荐赠送 (source_type: REFERRAL, action_type: EARN_REFERRAL)
   * @param {Object} data 
   */
  async addReferralReward(data) {
    return apiClient.post(API_ENDPOINTS.REWARDS.ADD_REFERRAL, data);
  },

  /**
   * 自动/手动写入达标赠送 (source_type: VOLUME_BONUS, action_type: EARN_VOLUME_BONUS)
   * @param {Object} data 
   */
  async addVolumeBonus(data) {
    return apiClient.post(API_ENDPOINTS.REWARDS.ADD_VOLUME_BONUS, data);
  },

  /**
   * 积攒满 1.0 时转为可用 (action_type: TRANSFER)
   * @param {Object} data 
   */
  async transferReward(data) {
    return apiClient.post(API_ENDPOINTS.REWARDS.TRANSFER, data);
  },

  /**
   * 签到核销赠课 (action_type: CONSUME)
   * @param {Object} data 
   */
  async consumeReward(data) {
    return apiClient.post(API_ENDPOINTS.REWARDS.CONSUME, data);
  },

  /**
   * 作废签到时，加回被核销的可用 (action_type: VOID_CONSUME)
   * @param {Object} data 
   */
  async voidConsume(data) {
    return apiClient.post(API_ENDPOINTS.REWARDS.VOID_CONSUME, data);
  },

  /**
   * 获取指定年份赠课统计 (Top 5+月度趋势)
   * @param {string} year - 年份或 'all'
   */
  async getStats(year) {
    return apiClient.get(API_ENDPOINTS.REWARDS.STATS, { year });
  },

  /**
   * 获取在途赠送列表
   * @param {number} studentId - 学员 ID
   */
  async getInTransit(studentId) {
    return apiClient.get(API_ENDPOINTS.REWARDS.IN_TRANSIT(studentId));
  },
  /**
   * 获取学生赠送概况列表
   * @param {string} year 
   * @param {number} page
   * @param {number} per_page
   * @param {number} studentId
   */
  async getSummary(year, page = 1, per_page = 20, studentId = null) {
    return apiClient.get(API_ENDPOINTS.REWARDS.SUMMARY, { 
      year, 
      page, 
      per_page,
      student_id: studentId
    });
  },
  /**
   * 获取所有产生过流水的学员列表，用于快速筛选
   * @param {string} year 
   */
  async getActiveStudents(year) {
    return apiClient.get(API_ENDPOINTS.REWARDS.ACTIVE_STUDENTS, { year });
  },

  /**
   * 获取学员发起的推荐列表 (包含被推荐人姓名、赠送金额、当前状态)
   * @param {number} studentId - 学员 ID
   */
  async getReferrals(studentId) {
    if (!studentId) return [];
    return apiClient.get(`/api/rewards/referrals/${studentId}`);
  },
};

export default rewardService;
